import type { ComponentProps } from 'react'

import { Breadcrumbs } from '@components/layouts/Breadcrumbs'

import type { ComponentDoc, PropDocs } from '../types'

type BreadcrumbsProps = ComponentProps<typeof Breadcrumbs>

function FullWidthBreadcrumbs(props: BreadcrumbsProps) {
  return (
    <div className="tw:w-full tw:bg-white">
      <Breadcrumbs {...props} />
    </div>
  )
}

const props: PropDocs<BreadcrumbsProps> = {
  items: {
    type: 'Array<{ label: string; href: string }>',
    required: true,
    description: 'Top level first. The last item is the current page and is not a link.',
  },
  linkComponent: {
    type: 'ElementType<{ href: string }>',
    default: "'a'",
    description: 'Renders each link. SidebarLayout passes the Inertia Link.',
  },
  spriteUrl: {
    type: 'string',
    description: 'Icon sprite URL. Only needed on v1 pages.',
  },
}

const doc: ComponentDoc<BreadcrumbsProps> = {
  slug: 'breadcrumbs',
  name: 'Breadcrumbs',
  tier: 'layout',
  description:
    'The trail above the page header. The layouts render it for you. To give a page breadcrumbs, add it to config/breadcrumbs.rb.',
  importStatement: "import { Breadcrumbs } from '@components/layouts/Breadcrumbs'",
  sourcePath: 'app/frontend/components/layouts/Breadcrumbs.tsx',
  props,
  examples: [
    {
      title: 'Add a trail',
      note: 'Keys are controller_path#action. Each block returns [label, href] and runs on the controller, so ivars, params and route helpers work. Entries inside an under block sit below that parent.',
      render: () => (
        <FullWidthBreadcrumbs
          items={[
            { label: 'Settings', href: '/app/settings' },
            { label: 'Event types', href: '/event_types' },
            { label: 'Edit', href: '/event_types/5/edit' },
          ]}
        />
      ),
      code: `breadcrumb('settings') { [t('labels.settings'), configurations_path] }
under 'settings' do
  breadcrumb('event_types#index') { [t('labels.event_types'), event_types_path] }
end
under 'event_types#index' do
  breadcrumb('event_types#edit') { [t('links.edit'), edit_event_type_path(@event_type)] }
end`,
    },
    {
      title: 'Set a parent inline',
      note: 'parent: sets the parent without an under block. Pass a lambda when the parent depends on the request. Return nil for no parent.',
      render: () => (
        <FullWidthBreadcrumbs
          items={[
            { label: 'Email blasts', href: '/email_blasts' },
            { label: 'Review', href: '/email_blasts/8' },
            { label: 'Edit', href: '/email_blasts/8/edit' },
          ]}
        />
      ),
      code: `breadcrumb('e_blasts#edit', parent: 'e_blasts#show') { [t('links.edit'), edit_e_blast_path(@email_blast)] }

breadcrumb('events#show', parent: -> { 'action_groups#events' if @action_group }) do
  [@event.name, event_path(@event)]
end`,
    },
    {
      title: 'No trail',
      note: 'Add modals and partials to the no_breadcrumbs list at the end of config/breadcrumbs.rb, one line per controller. A trail of one crumb is hidden, since it only repeats the page title.',
      render: () => <FullWidthBreadcrumbs items={[{ label: 'Supporter groups', href: '/groups' }]} />,
      code: `%w[
  groups/supporters#new groups/supporters#show
  people#event_rsvp people#new
].each { no_breadcrumbs it }`,
    },
  ],
  usage: {
    do: [
      'Add a breadcrumb or no_breadcrumbs for every new routed action. A missing one raises an error outside production.',
      'Nest by the sidebar, not the URL. An event at /supporter_groups/1/events/2 still sits under Events.',
      "Reuse the page's heading or nav label. Only add a key to config/locales/v1/breadcrumbs.en.yml if there isn't one.",
      'Use full translation keys for any crumb that can be a parent. Relative keys like t(".breadcrumb") only work on the current page.',
      "Link to the page's canonical URL, with any tab param it needs.",
    ],
    dont: [
      "Render Breadcrumbs in a page. The layouts already do, so you'd get two.",
      'Build the trail in a controller or pass it as a prop. Every page gets it automatically.',
      'Use it to move between wizard steps or tabs. Use StepLayout or Tabs.',
      'Add a back button that repeats the parent crumb.',
    ],
  },
  a11y: [
    'A <nav aria-label="breadcrumb"> around an ordered list, so screen readers can jump to it.',
    'The current page is a <span aria-current="page">, not a link.',
    'Separators are hidden from screen readers.',
    'On v2 pages the last crumb sets the tab title, unless PageLayout.Header sets it.',
  ],
}

export default doc
